import { useState, useEffect, useCallback } from 'react';
import { WORDS, CAT_COLORS, TYPE_LABEL } from '../data/words';
import { COLORS } from '../config';

// ─────────────────────────────────────────────
// localStorage helpers
// ─────────────────────────────────────────────
const LS = {
  get: (k, fallback) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : fallback; } catch { return fallback; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} },
};

function todayKey() { return new Date().toISOString().slice(0, 10); }

function updateStreak() {
  const today = todayKey();
  const data = LS.get('tz_streak', { last: null, count: 0, best: 0 });
  if (data.last === today) return data;
  const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
  const yKey = yesterday.toISOString().slice(0, 10);
  const newCount = data.last === yKey ? data.count + 1 : 1;
  const newBest = Math.max(newCount, data.best);
  const updated = { last: today, count: newCount, best: newBest };
  LS.set('tz_streak', updated);
  return updated;
}

function getDailyWord() {
  const dayNum = Math.floor(Date.now() / 86400000);
  return WORDS[dayNum % WORDS.length];
}

function generateQuiz(word, allWords) {
  const distractors = allWords
    .filter(w => w.rank !== word.rank && w.cat === word.cat)
    .sort(() => Math.random() - 0.5)
    .slice(0, 3)
    .map(w => w.eng);
  while (distractors.length < 3) {
    const r = allWords[Math.floor(Math.random() * allWords.length)];
    if (r.rank !== word.rank && !distractors.includes(r.eng)) distractors.push(r.eng);
  }
  const options = [...distractors, word.eng].sort(() => Math.random() - 0.5);
  return { word, options, correct: word.eng };
}

const btnBase = {
  padding:'12px 20px', borderRadius:'10px',
  border:'none', cursor:'pointer', fontSize:'0.875rem', fontWeight:600,
  minHeight:'48px', transition:'transform 0.15s cubic-bezier(0.16,1,0.3,1)',
};

// ─── STREAK WIDGET ───
function StreakBar({ streak, masteredCount, pct }) {
  return (
    <div style={{ display:'flex', gap:'24px', alignItems:'center', marginBottom:'24px', padding:'0 4px' }}>
      <div style={{ display:'flex', alignItems:'baseline', gap:'6px' }}>
        <span style={{ fontSize:'1.5rem', fontWeight:800, color: COLORS.primary, lineHeight:1 }}>{streak.count}</span>
        <span style={{ fontSize:'0.75rem', color: COLORS.textMuted }}>day streak</span>
      </div>
      {streak.best > 0 && (
        <div style={{ display:'flex', alignItems:'baseline', gap:'6px' }}>
          <span style={{ fontSize:'1.5rem', fontWeight:800, color: COLORS.textMuted, lineHeight:1 }}>{streak.best}</span>
          <span style={{ fontSize:'0.75rem', color: COLORS.textMuted }}>best</span>
        </div>
      )}
      <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:'8px' }}>
        <div style={{ height:'6px', width:'80px', borderRadius:'3px', background: COLORS.border, overflow:'hidden' }}>
          <div style={{ height:'100%', borderRadius:'3px', background: COLORS.primary, width:`${pct}%`, transition:'width 0.4s cubic-bezier(0.16,1,0.3,1)' }} />
        </div>
        <span style={{ fontSize:'0.75rem', fontWeight:700, color: COLORS.primary }}>{masteredCount}/{WORDS.length}</span>
      </div>
    </div>
  );
}

// ─── DAILY WORD ───
function DailyWord({ onLearnIt }) {
  const word = getDailyWord();
  const [revealed, setRevealed] = useState(false);
  return (
    <div style={{ marginBottom:'32px', paddingBottom:'24px', borderBottom:`1px solid ${COLORS.border}` }}>
      <div style={{ fontSize:'0.6875rem', fontWeight:600, letterSpacing:'0.05em', color: COLORS.primary, marginBottom:'12px', textTransform:'uppercase' }}>Word of the Day</div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'12px' }}>
        <div>
          <div style={{ fontSize:'clamp(2.5rem,8vw,3.5rem)', color: COLORS.tamilGold, fontWeight:700, lineHeight:1.1, fontFamily:"'Noto Serif Tamil', serif" }}>{word.tamil}</div>
          <div style={{ fontSize:'0.875rem', color: COLORS.textMuted, fontFamily:'monospace', marginTop:'4px' }}>{word.roman}</div>
        </div>
        <span style={{ padding:'4px 10px', borderRadius:'6px', background: CAT_COLORS[word.cat]?.bg, color: CAT_COLORS[word.cat]?.text, fontSize:'0.6875rem', fontWeight:600 }}>{word.cat}</span>
      </div>
      {revealed ? (
        <div style={{ marginTop:'12px' }}>
          <div style={{ fontSize:'1.125rem', fontWeight:700, color: COLORS.textPrimary, marginBottom:'6px' }}>{word.eng}</div>
          <div style={{ fontSize:'0.8125rem', color: COLORS.textSecondary, lineHeight:1.6 }}>{word.tip}</div>
          <button onClick={() => onLearnIt(word)} style={{ ...btnBase, marginTop:'12px', background:'transparent', border:`1.5px solid ${COLORS.border}`, color: COLORS.textSecondary, padding:'8px 16px', fontSize:'0.8125rem' }}>See in full list</button>
        </div>
      ) : (
        <button onClick={() => setRevealed(true)} style={{ ...btnBase, marginTop:'12px', background: COLORS.primaryLight, color: COLORS.primary, padding:'10px 20px', fontSize:'0.8125rem' }}>Reveal meaning</button>
      )}
    </div>
  );
}

// ─── QUIZ MODE ───
function QuizMode({ filtered }) {
  const [qIdx, setQIdx]       = useState(0);
  const [questions, setQs]    = useState([]);
  const [chosen, setChosen]   = useState(null);
  const [score, setScore]     = useState(0);
  const [done, setDone]       = useState(false);
  const [showTip, setShowTip] = useState(false);
  const TOTAL = 10;

  useEffect(() => {
    const pool = [...filtered].sort(() => Math.random() - 0.5).slice(0, TOTAL);
    setQs(pool.map(w => generateQuiz(w, WORDS)));
    setQIdx(0); setChosen(null); setScore(0); setDone(false);
  }, [filtered]);

  if (!questions.length) return null;
  const q = questions[qIdx];
  if (!q) return null;

  const handleAnswer = (opt) => {
    if (chosen) return;
    setChosen(opt);
    if (opt === q.correct) setScore(s => s + 1);
    const prev = LS.get('tz_quiz_best', 0);
    if (opt === q.correct && score + 1 > prev) LS.set('tz_quiz_best', score + 1);
  };

  const next = () => {
    setShowTip(false);
    if (qIdx + 1 >= questions.length) { setDone(true); return; }
    setQIdx(i => i + 1);
    setChosen(null);
  };

  if (done) {
    const best = LS.get('tz_quiz_best', score);
    const pct = Math.round((score / TOTAL) * 100);
    return (
      <div style={{ textAlign:'center', padding:'40px 20px' }}>
        <div style={{ fontSize:'3rem', marginBottom:'16px' }}>{pct >= 80 ? '🎉' : pct >= 50 ? '👍' : '📚'}</div>
        <div style={{ fontSize:'2.5rem', fontWeight:800, color: COLORS.textPrimary, marginBottom:'4px' }}>{score}/{TOTAL}</div>
        <div style={{ fontSize:'0.875rem', color: COLORS.textMuted, marginBottom:'4px' }}>{pct}% correct</div>
        <div style={{ fontSize:'0.75rem', color: COLORS.textMuted, marginBottom:'32px' }}>Best ever: {Math.max(best, score)}/{TOTAL}</div>
        {pct === 100 && <div style={{ fontSize:'0.875rem', color: COLORS.accent, fontWeight:600, marginBottom:'16px' }}>Perfect score! You're a Tamil master!</div>}
        {pct >= 80 && pct < 100 && <div style={{ fontSize:'0.875rem', color: COLORS.success, fontWeight:600, marginBottom:'16px' }}>Excellent! Almost perfect!</div>}
        {pct < 50 && <div style={{ fontSize:'0.875rem', color: COLORS.accent, fontWeight:600, marginBottom:'16px' }}>Keep practising. You're getting there!</div>}
        <button onClick={() => { setQs([...filtered].sort(()=>Math.random()-0.5).slice(0,TOTAL).map(w=>generateQuiz(w,WORDS))); setQIdx(0);setChosen(null);setScore(0);setDone(false); }} style={{ ...btnBase, background: COLORS.primary, color:'#fff', padding:'14px 32px' }}>
          Play Again
        </button>
      </div>
    );
  }

  const isCorrect = chosen === q.correct;
  const cc = CAT_COLORS[q.word.cat] || {};

  return (
    <div style={{ maxWidth:'420px', margin:'0 auto' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
        <div style={{ fontSize:'0.8125rem', color: COLORS.textMuted, fontWeight:500 }}>{qIdx + 1} of {TOTAL}</div>
        <div style={{ fontSize:'0.8125rem', color: COLORS.success, fontWeight:700 }}>{score} correct</div>
      </div>
      <div style={{ height:'4px', borderRadius:'2px', background: COLORS.border, marginBottom:'24px', overflow:'hidden' }}>
        <div style={{ height:'100%', borderRadius:'2px', background: COLORS.primary, width:`${((qIdx)/TOTAL)*100}%`, transition:'width 0.3s cubic-bezier(0.16,1,0.3,1)' }} />
      </div>

      {/* Question — no heavy card, just subtle bg */}
      <div style={{ padding:'28px 24px', borderRadius:'16px', background: cc.bg || COLORS.bgAlt, textAlign:'center', marginBottom:'16px' }}>
        <div style={{ fontSize:'0.6875rem', letterSpacing:'0.05em', color: cc.text || COLORS.textMuted, marginBottom:'16px', fontWeight:600, textTransform:'uppercase' }}>What does this mean?</div>
        <div style={{ fontSize:'clamp(2.5rem,12vw,4rem)', color: COLORS.tamilGold, fontWeight:700, lineHeight:1.1, fontFamily:"'Noto Serif Tamil', serif" }}>{q.word.tamil}</div>
        <div style={{ fontSize:'1rem', color: COLORS.textMuted, fontFamily:'monospace', marginTop:'8px' }}>{q.word.roman}</div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'16px' }}>
        {q.options.map((opt, i) => {
          let bg = COLORS.bgAlt, border = 'transparent', color = COLORS.textPrimary;
          if (chosen) {
            if (opt === q.correct) { bg = COLORS.successLight; border = COLORS.success; color = '#166534'; }
            else if (opt === chosen) { bg = '#FEE2E2'; border = '#EF4444'; color = '#991B1B'; }
          }
          return (
            <button key={i} onClick={() => handleAnswer(opt)} style={{
              padding:'16px 12px', borderRadius:'12px', background: bg, border:`2px solid ${border}`, color,
              cursor: chosen?'default':'pointer', fontSize:'0.875rem', fontWeight:600,
              textAlign:'center', transition:'all 0.2s cubic-bezier(0.16,1,0.3,1)',
              opacity: chosen && opt !== q.correct && opt !== chosen ? 0.4 : 1,
              minHeight:'56px',
            }}>{opt}</button>
          );
        })}
      </div>

      {chosen && (
        <div style={{ padding:'16px', borderRadius:'12px', background: isCorrect ? COLORS.successLight : '#FEE2E2', marginBottom:'12px' }}>
          <div style={{ fontSize:'0.875rem', fontWeight:700, color: isCorrect ? COLORS.success : '#DC2626', marginBottom:'4px' }}>
            {isCorrect ? 'Correct!' : `The answer was: ${q.correct}`}
          </div>
          {!showTip && <button onClick={() => setShowTip(true)} style={{ fontSize:'0.75rem', color: COLORS.primary, background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>Show memory tip</button>}
          {showTip && <div style={{ fontSize:'0.8125rem', color: COLORS.textSecondary, lineHeight:1.6, marginTop:'6px' }}>{q.word.tip}</div>}
        </div>
      )}

      {chosen && <button onClick={next} style={{ ...btnBase, width:'100%', background: COLORS.primary, color:'#fff' }}>{qIdx + 1 >= TOTAL ? 'See Results' : 'Next Question'}</button>}
    </div>
  );
}

// ─── GROUP CODE LEADERBOARD ───
function GroupMode({ mastered }) {
  const [code, setCode]       = useState(LS.get('tz_group_code', ''));
  const [codeInput, setCodeInput] = useState('');
  const [name, setName]       = useState(LS.get('tz_player_name', ''));
  const [nameInput, setNameInput] = useState('');
  const [board, setBoard]     = useState([]);
  const [joined, setJoined]   = useState(false);

  const masteredCount = Object.values(mastered).filter(Boolean).length;

  const join = () => {
    const c = codeInput.toUpperCase().slice(0, 4);
    const n = nameInput.trim() || 'Learner';
    if (!c) return;
    LS.set('tz_group_code', c);
    LS.set('tz_player_name', n);
    setCode(c); setName(n);
    const key = `tz_group_${c}`;
    const existing = LS.get(key, []);
    const updated = existing.filter(p => p.name !== n);
    updated.push({ name: n, mastered: masteredCount, date: todayKey() });
    updated.sort((a, b) => b.mastered - a.mastered);
    LS.set(key, updated.slice(0, 20));
    setBoard(updated);
    setJoined(true);
  };

  useEffect(() => {
    if (code) {
      const key = `tz_group_${code}`;
      const existing = LS.get(key, []);
      if (name) {
        const updated = existing.filter(p => p.name !== name);
        updated.push({ name, mastered: masteredCount, date: todayKey() });
        updated.sort((a, b) => b.mastered - a.mastered);
        LS.set(key, updated.slice(0, 20));
        setBoard(updated);
      } else {
        setBoard(existing);
      }
      setJoined(true);
    }
  }, [mastered, code, name, masteredCount]);

  const leave = () => { LS.set('tz_group_code', ''); LS.set('tz_player_name', ''); setCode(''); setName(''); setJoined(false); setBoard([]); };

  if (!joined) return (
    <div style={{ maxWidth:'400px' }}>
      <p style={{ fontSize:'0.8125rem', color: COLORS.textSecondary, lineHeight:1.6, marginBottom:'16px' }}>
        Share a 4-letter group code with your class or family. Everyone who enters the same code appears on the same leaderboard. <strong style={{ color: COLORS.primary }}>No login needed.</strong>
      </p>
      <div style={{ fontSize:'0.75rem', color: COLORS.textMuted, marginBottom:'16px', padding:'12px', background: COLORS.bgAlt, borderRadius:'8px' }}>
        On a single device, progress is shared automatically. Cross-device leaderboards coming soon!
      </div>
      <input value={nameInput} onChange={e => setNameInput(e.target.value)} placeholder="Your name"
        style={{ width:'100%', padding:'12px 16px', borderRadius:'10px', border:`1.5px solid ${COLORS.border}`, background:'transparent', color: COLORS.textPrimary, fontSize:'0.875rem', outline:'none', boxSizing:'border-box', marginBottom:'10px' }} />
      <div style={{ display:'flex', gap:'8px' }}>
        <input value={codeInput} onChange={e => setCodeInput(e.target.value.toUpperCase().slice(0,4))} placeholder="TAMI"
          style={{ flex:1, padding:'12px 16px', borderRadius:'10px', border:`1.5px solid ${COLORS.border}`, background:'transparent', color: COLORS.primary, fontSize:'1.125rem', fontWeight:700, letterSpacing:'4px', outline:'none', textTransform:'uppercase' }} />
        <button onClick={join} disabled={!codeInput.trim()} style={{ ...btnBase, background: codeInput ? COLORS.primary : COLORS.border, color: codeInput ? '#fff' : COLORS.textMuted, padding:'12px 24px' }}>Join</button>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
        <div>
          <span style={{ fontSize:'0.8125rem', color: COLORS.textMuted }}>Group </span>
          <span style={{ fontSize:'1.25rem', fontWeight:800, color: COLORS.primary, letterSpacing:'3px', fontFamily:'monospace' }}>{code}</span>
        </div>
        <button onClick={leave} style={{ fontSize:'0.75rem', color: COLORS.textMuted, background:'none', border:'none', cursor:'pointer', fontWeight:500 }}>Leave group</button>
      </div>
      {board.length === 0 ? (
        <div style={{ padding:'24px', color: COLORS.textMuted, fontSize:'0.875rem' }}>No scores yet. Start learning to appear here!</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
          {board.map((p, i) => (
            <div key={i} style={{
              display:'flex', alignItems:'center', gap:'12px', padding:'12px 16px', borderRadius:'10px',
              background: p.name === name ? COLORS.primaryLight : 'transparent',
            }}>
              <div style={{ fontSize:'1rem', minWidth:'28px', textAlign:'center', fontWeight:700, color: i < 3 ? COLORS.primary : COLORS.textMuted }}>
                {i === 0 ? '1st' : i === 1 ? '2nd' : i === 2 ? '3rd' : `${i+1}th`}
              </div>
              <div style={{ flex:1, fontSize:'0.875rem', fontWeight: p.name === name ? 700 : 500, color: COLORS.textPrimary }}>{p.name} {p.name === name ? '(you)' : ''}</div>
              <div style={{ fontSize:'0.8125rem', fontWeight:700, color: COLORS.primary }}>{p.mastered}</div>
              <div style={{ height:'4px', width:'48px', borderRadius:'2px', background: COLORS.border, overflow:'hidden' }}>
                <div style={{ height:'100%', background: COLORS.primary, width:`${(p.mastered/WORDS.length)*100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SUGGEST A WORD ───
function SuggestWord() {
  const [word, setWord]   = useState('');
  const [meaning, setMeaning] = useState('');
  const [sent, setSent]   = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!word.trim()) return;
    setSaving(true);
    try {
      // Save locally
      const existing = LS.get('tz_suggestions', []);
      const updated = [{ word: word.trim(), meaning: meaning.trim(), date: todayKey() }, ...existing].slice(0, 50);
      LS.set('tz_suggestions', updated);
      // Email via FormSubmit
      try {
        await fetch('https://formsubmit.co/ajax/shri.viratuk@gmail.com', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            _subject: 'tamizh.fun — New word suggestion',
            word: word.trim(),
            meaning: meaning.trim() || '(not provided)',
            date: new Date().toLocaleDateString('en-GB'),
          }),
        });
      } catch {}
    } catch {}
    setSent(true); setSaving(false); setWord(''); setMeaning('');
    setTimeout(() => setSent(false), 3000);
  };

  return (
    <div style={{ marginTop:'32px', paddingTop:'24px', borderTop:`1px solid ${COLORS.border}` }}>
      <div style={{ fontSize:'0.6875rem', fontWeight:600, color: COLORS.primary, letterSpacing:'0.05em', textTransform:'uppercase', marginBottom:'8px' }}>Missing a word?</div>
      {sent && <div style={{ padding:'10px 14px', borderRadius:'8px', background: COLORS.successLight, fontSize:'0.8125rem', color: COLORS.success, fontWeight:600, marginBottom:'12px' }}>Thanks! We'll review your suggestion.</div>}
      <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
        <input value={word} onChange={e => setWord(e.target.value)} placeholder="Tamil word or English meaning"
          style={{ flex:'1 1 160px', padding:'10px 14px', borderRadius:'8px', border:`1.5px solid ${COLORS.border}`, background:'transparent', color: COLORS.textPrimary, fontSize:'0.875rem', outline:'none' }} />
        <input value={meaning} onChange={e => setMeaning(e.target.value)} placeholder="Translation (optional)"
          style={{ flex:'1 1 140px', padding:'10px 14px', borderRadius:'8px', border:`1.5px solid ${COLORS.border}`, background:'transparent', color: COLORS.textPrimary, fontSize:'0.875rem', outline:'none' }} />
        <button onClick={submit} disabled={saving || !word.trim()} style={{
          ...btnBase, background: word.trim() ? COLORS.primary : COLORS.border,
          color: word.trim() ? '#fff' : COLORS.textMuted, padding:'10px 20px', fontSize:'0.8125rem',
          flexShrink:0,
        }}>{saving ? 'Sending...' : 'Suggest'}</button>
      </div>
    </div>
  );
}

// ─── MAIN VOCAB APP ───
const CATS = ["all","spoken","literary","Verb","Noun","Pronoun","Adjective","Question","Expression","Family"];

export default function VocabApp() {
  const [mode, setMode]       = useState('list');
  const [filter, setFilter]   = useState('all');
  const [cardIdx, setCardIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const [search, setSearch]   = useState('');
  const [mastered, setMastered] = useState(() => LS.get('tz_mastered', {}));
  const [streak, setStreak]   = useState(() => updateStreak());
  const [highlightRank, setHighlightRank] = useState(null);
  const [expandedRank, setExpandedRank] = useState(null);

  useEffect(() => { LS.set('tz_mastered', mastered); }, [mastered]);

  const filtered = WORDS.filter(w => {
    const mf = filter==='all'||filter===w.type||filter===w.cat;
    const ms = !search||w.eng.toLowerCase().includes(search.toLowerCase())||w.roman.toLowerCase().includes(search.toLowerCase())||w.tamil.includes(search);
    return mf && ms;
  });

  const idx = Math.min(cardIdx, Math.max(filtered.length-1, 0));
  const cw  = filtered[idx];
  const masteredCount = Object.values(mastered).filter(Boolean).length;
  const pct = Math.round(masteredCount/WORDS.length*100);

  const next = useCallback(() => { setFlipped(false); setShowTip(false); setCardIdx(i=>(i+1)%filtered.length); }, [filtered.length]);
  const prev = useCallback(() => { setFlipped(false); setShowTip(false); setCardIdx(i=>(i-1+filtered.length)%filtered.length); }, [filtered.length]);
  const toggleM = useCallback((rank) => setMastered(m => ({ ...m, [rank]: !m[rank] })), []);

  const goToWord = (word) => { setMode('list'); setFilter('all'); setSearch(''); setHighlightRank(word.rank); setTimeout(() => setHighlightRank(null), 3000); };

  const MODES = [['list','List'],['flashcard','Flashcards'],['quiz','Quiz'],['group','Group']];

  return (
    <div style={{ minHeight:'100vh', background: COLORS.bg, color: COLORS.textPrimary }}>
      {/* Header — minimal, not a big banner */}
      <div style={{ maxWidth:'960px', margin:'0 auto', padding:'24px 20px 0' }}>
        <h1 style={{ margin:'0 0 4px', fontSize:'1.75rem', fontWeight:800, color: COLORS.textPrimary, letterSpacing:'-0.02em' }}>Top 81 Tamil Words</h1>
        <div style={{ fontSize:'0.8125rem', color: COLORS.textMuted, marginBottom:'24px' }}>Frequency-ranked · No login · Free forever</div>

        <StreakBar streak={streak} masteredCount={masteredCount} pct={pct} />
        <DailyWord onLearnIt={goToWord} />

        {/* Mode tabs — underline style */}
        <div style={{ display:'flex', gap:'0', borderBottom:`2px solid ${COLORS.border}`, marginBottom:'24px' }}>
          {MODES.map(([m,label]) => (
            <button key={m} onClick={() => { setMode(m); setCardIdx(0); setFlipped(false); setShowTip(false); }} style={{
              padding:'10px 16px', background:'none', border:'none',
              borderBottom: mode===m ? `2px solid ${COLORS.primary}` : '2px solid transparent',
              color: mode===m ? COLORS.primary : COLORS.textMuted,
              fontWeight: mode===m ? 700 : 500, cursor:'pointer', fontSize:'0.875rem',
              marginBottom:'-2px', transition:'color 0.2s',
            }}>{label}</button>
          ))}
        </div>

        {/* Search */}
        {(mode==='list'||mode==='flashcard') && (
          <input value={search} onChange={e => { setSearch(e.target.value); setCardIdx(0); }}
            placeholder="Search Tamil, meaning or transliteration..."
            style={{ width:'100%', padding:'12px 16px', borderRadius:'10px', border:`1.5px solid ${COLORS.border}`, background:'transparent', color: COLORS.textPrimary, fontSize:'0.875rem', outline:'none', boxSizing:'border-box', marginBottom:'12px' }} />
        )}

        {/* Filter pills */}
        {(mode==='list'||mode==='flashcard') && (
          <div style={{ display:'flex', gap:'6px', overflowX:'auto', paddingBottom:'8px', marginBottom:'20px', scrollbarWidth:'none', WebkitOverflowScrolling:'touch' }}>
            {CATS.map(c => {
              const cnt = c==='all' ? WORDS.length : WORDS.filter(w=>w.cat===c||w.type===c).length;
              const col = CAT_COLORS[c];
              return (
                <button key={c} onClick={() => { setFilter(c); setCardIdx(0); setFlipped(false); }} style={{
                  whiteSpace:'nowrap', padding:'6px 12px', borderRadius:'6px', flexShrink:0,
                  border:'none',
                  background: filter===c ? (col?.bg || COLORS.primaryLight) : COLORS.bgAlt,
                  color: filter===c ? (col?.text || COLORS.primary) : COLORS.textMuted,
                  fontWeight: filter===c ? 700 : 500, cursor:'pointer', fontSize:'0.75rem',
                  transition:'all 0.2s',
                }}>{c==='all'?`All (${cnt})`:`${c} (${cnt})`}</button>
              );
            })}
          </div>
        )}

        {/* ── LIST MODE ── */}
        {mode==='list' && (
          <>
            <div style={{ marginBottom:'12px', fontSize:'0.75rem', color: COLORS.textMuted }}>{filtered.length} words · Tap word to see example · Long press to mark mastered</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:'8px' }}>
              {filtered.map(w => {
                const c = CAT_COLORS[w.cat] || {};
                const isMastered = mastered[w.rank];
                const isHighlighted = w.rank === highlightRank;
                const isExpanded = expandedRank === w.rank;
                return (
                  <div key={w.rank} onClick={() => setExpandedRank(isExpanded ? null : w.rank)} onDoubleClick={() => toggleM(w.rank)} style={{
                    background: isMastered ? COLORS.successLight : isHighlighted ? COLORS.primaryLight : COLORS.bgCard,
                    border: isHighlighted ? `1.5px solid ${COLORS.primary}` : '1.5px solid transparent',
                    borderRadius:'12px', padding:'16px', cursor:'pointer',
                    opacity: isMastered ? 0.65 : 1, position:'relative',
                    overflow:'hidden',
                    transition:'all 0.2s cubic-bezier(0.16,1,0.3,1)',
                    boxShadow: isHighlighted ? `0 0 0 3px ${COLORS.primaryLight}` : `0 1px 3px ${COLORS.shadow}`,
                  }}>
                    <div style={{ fontSize:'clamp(0.875rem,3vw,1.125rem)', color: COLORS.tamilGold, fontWeight:700, lineHeight:1.2, fontFamily:"'Noto Serif Tamil', serif", overflowWrap:'break-word' }}>{w.tamil}</div>
                    <div style={{ fontSize:'0.625rem', color: COLORS.textMuted, fontFamily:'monospace', marginTop:'2px' }}>{w.roman}</div>
                    <div style={{ fontSize:'0.75rem', color: COLORS.textPrimary, fontWeight:600, marginTop:'4px' }}>{w.eng}</div>
                    {isExpanded && w.eg && (
                      <div style={{ marginTop:'6px', padding:'6px 8px', borderRadius:'6px', background: COLORS.bgAlt, borderLeft:`2px solid ${COLORS.tamilGold}` }}>
                        <div style={{ fontSize:'0.6875rem', color: COLORS.tamilGold, fontFamily:"'Noto Serif Tamil', serif", lineHeight:1.4 }}>{w.eg}</div>
                        <div style={{ fontSize:'0.625rem', color: COLORS.textMuted, marginTop:'2px' }}>{w.egEn}</div>
                      </div>
                    )}
                    <div style={{ marginTop:'6px', display:'flex', gap:'4px', flexWrap:'wrap' }}>
                      <span style={{ fontSize:'0.5625rem', padding:'2px 6px', borderRadius:'4px', background: c.bg, color: c.text, fontWeight:600 }}>{w.cat}</span>
                      <span style={{ fontSize:'0.5625rem', padding:'2px 6px', borderRadius:'4px', background: COLORS.bgAlt, color: COLORS.textMuted }}>{TYPE_LABEL[w.type]}</span>
                    </div>
                    <div style={{ position:'absolute', top:'8px', right:'10px', fontSize:'0.5625rem', color: COLORS.border, fontWeight:500 }}>#{w.rank}</div>
                    {isMastered && <div style={{ position:'absolute', bottom:'8px', right:'10px', fontSize:'0.8125rem', color: COLORS.success, fontWeight:700 }}>✓</div>}
                  </div>
                );
              })}
            </div>
            <SuggestWord />
          </>
        )}

        {/* ── FLASHCARD MODE ── */}
        {mode==='flashcard' && cw && (
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'16px' }}>
            <div style={{ fontSize:'0.75rem', color: COLORS.textMuted, fontWeight:500 }}>{idx+1} of {filtered.length}</div>
            <div onClick={() => setFlipped(f=>!f)} style={{
              width:'min(380px,92vw)', minHeight:'220px', borderRadius:'16px',
              background: flipped ? (CAT_COLORS[cw.cat]?.bg || COLORS.bgAlt) : COLORS.bgCard,
              boxShadow:`0 4px 24px ${COLORS.shadow}`,
              display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
              cursor:'pointer', padding:'32px 24px',
              textAlign:'center', gap:'8px', userSelect:'none',
              transition:'background 0.3s cubic-bezier(0.16,1,0.3,1)',
            }}>
              {!flipped ? (
                <>
                  <div style={{ fontSize:'clamp(2.5rem,13vw,4.5rem)', color: COLORS.tamilGold, fontWeight:700, lineHeight:1.1, fontFamily:"'Noto Serif Tamil', serif" }}>{cw.tamil}</div>
                  <div style={{ fontSize:'1rem', color: COLORS.textMuted, fontFamily:'monospace' }}>{cw.roman}</div>
                  <div style={{ fontSize:'0.6875rem', color: COLORS.border, marginTop:'8px' }}>tap to reveal</div>
                </>
              ) : (
                <>
                  <div style={{ fontSize:'clamp(1.25rem,5vw,1.75rem)', fontWeight:700, color: CAT_COLORS[cw.cat]?.text || COLORS.textPrimary }}>{cw.eng}</div>
                  <span style={{ padding:'4px 10px', borderRadius:'6px', background: CAT_COLORS[cw.cat]?.bg, color: CAT_COLORS[cw.cat]?.text, fontSize:'0.6875rem', fontWeight:600 }}>{cw.cat}</span>
                  <div style={{ fontSize:'0.6875rem', color: COLORS.textMuted }}>{TYPE_LABEL[cw.type]} · Rank #{cw.rank}</div>
                </>
              )}
            </div>
            <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', justifyContent:'center' }}>
              <button onClick={prev} style={{ ...btnBase, background: COLORS.bgAlt, color: COLORS.textSecondary }}>Prev</button>
              <button onClick={() => toggleM(cw.rank)} style={{ ...btnBase, background: mastered[cw.rank] ? COLORS.success : COLORS.primaryLight, color: mastered[cw.rank] ? '#fff' : COLORS.primary }}>
                {mastered[cw.rank]?'Mastered':'Mark Mastered'}
              </button>
              <button onClick={next} style={{ ...btnBase, background: COLORS.primary, color:'#fff' }}>Next</button>
            </div>
            <div style={{ width:'min(380px,92vw)' }}>
              <button onClick={() => setShowTip(t=>!t)} style={{ ...btnBase, width:'100%', background:'transparent', border:`1.5px solid ${COLORS.border}`, color: COLORS.textSecondary }}>
                {showTip?'Hide Memory Tip':'Show Memory Tip'}
              </button>
              {showTip && cw.tip && <div style={{ marginTop:'12px', padding:'16px', borderRadius:'10px', background: COLORS.bgAlt, fontSize:'0.8125rem', lineHeight:1.7, color: COLORS.textSecondary }}>{cw.tip}</div>}
            </div>
          </div>
        )}

        {/* ── QUIZ MODE ── */}
        {mode==='quiz' && (
          <div>
            <div style={{ marginBottom:'16px', fontSize:'0.8125rem', color: COLORS.textMuted }}>
              10 random questions from your current filter · Best: {LS.get('tz_quiz_best',0)}/10
            </div>
            <QuizMode filtered={filtered} />
          </div>
        )}

        {/* ── GROUP MODE ── */}
        {mode==='group' && (
          <div>
            <h3 style={{ color: COLORS.textPrimary, marginBottom:'12px', fontSize:'1.125rem', fontWeight:700 }}>Group Leaderboard</h3>
            <GroupMode mastered={mastered} />
          </div>
        )}

        {/* Footer — minimal */}
        <div style={{ marginTop:'48px', paddingTop:'24px', paddingBottom:'48px', borderTop:`1px solid ${COLORS.border}`, fontSize:'0.75rem', color: COLORS.textMuted }}>
          Supported by <a href="https://virat.org.uk" target="_blank" rel="noopener noreferrer" style={{ color: COLORS.primary, textDecoration:'none', fontWeight:600 }}>VIRAT UK</a> · Preserving Indian heritage through education
        </div>
      </div>
    </div>
  );
}
